import React from 'react';
import { Box, Paper, Typography, Button, Divider, Card } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useTranslation } from 'react-i18next';

interface DownloadFilesBoxProps {
    isPaid: boolean;
    onDownloadPdf?: () => void;
    onDownloadExcel?: () => void;
    onDownloadForms?: () => void;
    isDownloadingPdf?: boolean;
    isDownloadingExcel?: boolean;
    isDownloadingForms?: boolean;
    isPreparingDownload?: boolean;
}

const DownloadFilesBox: React.FC<DownloadFilesBoxProps> = ({
    isPaid,
    onDownloadPdf,
    onDownloadExcel,
    onDownloadForms,
    isDownloadingPdf = false,
    isDownloadingExcel = false,
    isDownloadingForms = false,
    isPreparingDownload = false
}) => {
    const { t } = useTranslation();

    const isLoading = isDownloadingPdf || isDownloadingExcel || isDownloadingForms || isPreparingDownload;

    if (isPaid) {
        // SUCCESS STATE - Modern card-based design with three download types
        return (
            <Card sx={{}}>
                {/* Download Cards (Unlocked - Direct content without header) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* PDF Tax Report */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        p: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                            <PictureAsPdfIcon sx={{ color: 'error.main', fontSize: 20, mt: 0.2 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1">
                                    Für Steuerberater & Finanzamt
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Kompletter Steuerbericht als PDF mit allen Transaktionen und Berechnungen.
                                </Typography>
                            </Box>
                        </Box>
                        <Button 
                            variant="contained" 
                            color="error"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={onDownloadPdf}
                            disabled={isLoading}
                            sx={{ 
                                ml: 2
                            }}
                        >
                            {isDownloadingPdf ? t('taxReports.downloading', 'Laden...') : 'PDF'}
                        </Button>
                    </Box>

                    {/* Excel Tax Report */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        p: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                            <TableChartIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.2 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1">
                                    Analysen & Archivierung
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Alle Daten und Berechnungen in Excel-Tabellen.
                                </Typography>
                            </Box>
                        </Box>
                        <Button 
                            variant="contained" 
                            color="success"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={onDownloadExcel}
                            disabled={isLoading}
                            sx={{ 
                                ml: 2
                            }}
                        >
                            {isDownloadingExcel ? t('taxReports.downloading', 'Laden...') : 'Excel'}
                        </Button>
                    </Box>

                    {/* Tax Forms */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        p: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                            <AssignmentIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.2 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1">
                                    Direkt für die Steuererklärung
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    BMF-konforme Steuerformulare (Anlage SO & KAP) für Deutschland.
                                </Typography>
                            </Box>
                        </Box>
                        <Button 
                            variant="contained" 
                            color="primary"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={onDownloadForms}
                            disabled={isLoading || !onDownloadForms}
                            sx={{ 
                                ml: 2
                            }}
                        >
                            {isDownloadingForms ? t('taxReports.downloading', 'Laden...') : 'PDF'}
                        </Button>
                    </Box>
                </Box>
            </Card>
        );
    }

    // LOCKED STATE - Modern locked design matching the unlocked version
    return (
        <Card sx={{}}>
            {/* Download Cards (Locked - Direct content without header) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* PDF Tax Report */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <PictureAsPdfIcon sx={{ color: 'error.main', fontSize: 20, mt: 0.2 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">
                                Für Steuerberater & Finanzamt
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Kompletter Steuerbericht als PDF mit allen Transaktionen und Berechnungen.
                            </Typography>
                        </Box>
                    </Box>
                    <LockIcon sx={{ 
                        color: 'text.secondary', 
                        fontSize: 20,
                        ml: 2
                    }} />
                </Box>

                {/* Excel Tax Report */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <TableChartIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.2 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">
                                Analysen & Archivierung
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Alle Daten und Berechnungen in Excel-Tabellen.
                            </Typography>
                        </Box>
                    </Box>
                    <LockIcon sx={{ 
                        color: 'text.secondary', 
                        fontSize: 20,
                        ml: 2
                    }} />
                </Box>

                {/* Tax Forms */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <AssignmentIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.2 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">
                                Direkt für die Steuererklärung
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                BMF-konforme Steuerformulare (Anlage SO & KAP) für Deutschland.
                            </Typography>
                        </Box>
                    </Box>
                    <LockIcon sx={{ 
                        color: 'text.secondary', 
                        fontSize: 20,
                        ml: 2
                    }} />
                </Box>
            </Box>
        </Card>
    );
};

export default DownloadFilesBox;